/**
 * @file    projectFrequency.ts
 * @scope   SHARED
 * @summary Constants + builders for the pinned "Project Frequency" field — the special field that
 *          exists on every project, is auto-supplied (not user-selectable) and counts documents.
 * @consumes SelectedFieldModel
 * @produces PROJECT_FREQUENCY_* constants, display-name builder, field builder + type guards
 * @sideEffects None — pure.
 * @notes This field is intentionally excluded from the selectable field list; its calculation
 *        config is injected automatically at serialization time.
 */
import { SelectedFieldModel } from 'models/Enterprise/FieldsManagerModel';

/**
 * Backend field name used by the pinned "Project Frequency" calculation.
 * This field exists on every project and is intentionally excluded from the
 * selectable field list — it is supplied automatically.
 */
export const PROJECT_FREQUENCY_FIELD_NAME = 'complete_date';

/**
 * Calculation type identifier for the "Project Frequency" calculation.
 * Counts the documents that have data at the FieldName path.
 */
export const PROJECT_FREQUENCY_CALC_TYPE = 'Frequency/TotalSurveys';

/**
 * User-facing label describing the pinned field.
 */
export const PROJECT_FREQUENCY_LABEL = 'Project Frequency';
export const PROJECT_FREQUENCY_SUFFIX = 'frequency';

/**
 * Builds the default display name for the pinned project frequency field.
 * @param partitionLabel - Label of the selected partition
 * @returns "{partitionLabel} frequency" when label exists, otherwise "frequency"
 */
export const buildProjectFrequencyDisplayName = (partitionLabel = ''): string => {
  const normalizedPartitionLabel = partitionLabel.trim();
  if (!normalizedPartitionLabel) return PROJECT_FREQUENCY_SUFFIX;
  return `${normalizedPartitionLabel} ${PROJECT_FREQUENCY_SUFFIX}`;
};

/**
 * Builds the pinned "Project Frequency" field entry.
 * The user only configures the Display Name; all other values are fixed.
 * @param displayName - Optional pre-filled display name (used during edit-mode recovery)
 * @returns A SelectedFieldModel flagged as the project frequency field
 */
export const buildProjectFrequencyField = (displayName = ''): SelectedFieldModel => ({
  fieldNumber: 1,
  field: {
    fieldGuid: '',
    fieldName: '',
    columnName: PROJECT_FREQUENCY_FIELD_NAME,
    typeName: PROJECT_FREQUENCY_CALC_TYPE,
  },
  displayName,
  calculationType: {
    displayName: '',
    fieldName: PROJECT_FREQUENCY_FIELD_NAME,
    type: '5',
  },
  precision: '0',
  normalize: false,
  statTesting: false,
  isL1Category: true,
  isProjectFrequency: true,
});

/**
 * Detects whether a stored queryCollections field is a persisted project frequency field.
 * @param storedField - A field entry as stored in queryCollections[n].fields
 * @returns true if the stored field represents the project frequency calculation
 */
export const isStoredProjectFrequencyField = (storedField: any): boolean =>
  storedField?.calcType === PROJECT_FREQUENCY_CALC_TYPE ||
  storedField?.fieldName === PROJECT_FREQUENCY_FIELD_NAME;

/**
 * Ensures the pinned project frequency field is present exactly once at index 0.
 * Preserves an existing project frequency field's display name if one is already present.
 *
 * IDEMPOTENT BY REFERENCE: when the list is already correct (a single project frequency field
 * sitting at index 0 with a non-empty display name), the very same `fields` array reference is
 * returned. This is REQUIRED because the caller decides whether to persist by comparing element
 * references (`ensured[i] !== fields[i]`). Returning a fresh head object on every call made that
 * comparison always true, triggering an endless `updateUserFields` dispatch loop ("Maximum update
 * depth exceeded") and re-writing stale snapshots that resurrected stripped fields.
 *
 * @param fields - The current list of selected fields
 * @param partitionLabel - The label of the parent partition, used to pre-fill the display name
 * @returns The same array when already correct, otherwise a new array starting with the PF field
 */
export const ensureProjectFrequencyField = (
  fields: SelectedFieldModel[],
  partitionLabel: string
): SelectedFieldModel[] => {
  const existing = fields.find((f) => f.isProjectFrequency);
  const defaultDisplayName = buildProjectFrequencyDisplayName(partitionLabel);

  if (!existing) {
    const rest = fields.filter((f) => !f.isProjectFrequency);
    return [buildProjectFrequencyField(defaultDisplayName), ...rest];
  }

  // Only allocate a new head object when the display name actually needs filling in.
  const head = existing.displayName?.trim()
    ? existing
    : { ...existing, displayName: defaultDisplayName };

  const projectFrequencyCount = fields.reduce((n, f) => (f.isProjectFrequency ? n + 1 : n), 0);
  const isAlreadyCorrect = fields[0] === head && projectFrequencyCount === 1;
  if (isAlreadyCorrect) return fields;

  const rest = fields.filter((f) => !f.isProjectFrequency);
  return [head, ...rest];
};
